import { prefersReducedMotion } from "../reducedMotion";

/** The theme's animation of a jump's target (`[data-jumped]`, theme/base/layout.css): the mark lasts as long. */
export const JUMP_MARK_ANIMATION = "app-jump-landed";

/** Ends the mark once its own animation ends, not a descendant's. */
function unmarkWhenShown(event: Event): void {
  const element = event.currentTarget;
  if (event.target !== element || !(element instanceof HTMLElement) || (event as AnimationEvent).animationName !== JUMP_MARK_ANIMATION) return;
  element.removeAttribute("data-jumped");
  element.removeEventListener("animationend", unmarkWhenShown);
}

export interface JumpOptions {
  /** Whether keyboard focus moves to the target: not where the person didn't ask for the jump here (default: yes). */
  readonly takeFocus?: boolean;
}

/**
 * Brings an element of this page into view, moves keyboard focus to it and marks it briefly, so a jump visibly lands,
 * also when its target was already in view (F164). Scrolls at once where the person asks for reduced motion; the
 * theme's mark then holds rather than fades. A data attribute, not a class: React keeps an element's `className`
 * and would drop an added class on its next change.
 */
export function jumpTo(target: HTMLElement, block: ScrollLogicalPosition, { takeFocus = true }: JumpOptions = {}): void {
  target.scrollIntoView({ block, behavior: prefersReducedMotion() ? "auto" : "smooth" });
  if (takeFocus) {
    // Focus reaches an element that isn't focusable only from code, never by Tab.
    if (!target.hasAttribute("tabindex")) target.tabIndex = -1;
    target.focus({ preventScroll: true });
  }
  // Off for a frame, so the mark plays again on a target still marked; the same listener is added once.
  target.removeAttribute("data-jumped");
  target.addEventListener("animationend", unmarkWhenShown);
  requestAnimationFrame(() => { target.setAttribute("data-jumped", ""); });
}
